<script setup>
import { router, Head, Link, useForm, usePage } from '@inertiajs/vue3';
import { reactive, ref } from 'vue';
import JetButton from '@/Jetstream/Button.vue';
import JetReportingHeader from '@/Jetstream/ReportingHeader.vue';
import AppTheming from '@/Jetstream/AppTheming.vue';
import TeamAccordion from '@/Pages/Reporting/TeamAccordion.vue';
import ReportingCarbonSavingsWidget from '@/Pages/Reporting/ReportingCarbonSavingsWidget.vue';



// includes we need to front end locale magic
import { useI18n } from 'vue-i18n';

// Get locale as set in cookie
const { locale, fallbackLocale} = useI18n({ useScope: 'global' });

const props = defineProps({
    supervisor: Object,
    matchingTeamsGroupedbyOrganisation: Object,
    });

const supervisor = props.supervisor;

const matchingTeamsGroupedbyOrganisation = props.matchingTeamsGroupedbyOrganisation;
let supervisorRegionOrOrg = '';

//console.log(supervisor, matchingTeamsGroupedbyOrganisation);

if (supervisor.supervisor_type == 'icb_supervisor') {
    supervisorRegionOrOrg = supervisor.supervisor_icb_region;
}
else if (supervisor.supervisor_type == 'private_supervisor') {
    supervisorRegionOrOrg = supervisor.supervisor_private_org;
}
else {}

function formatDate(dateString) {
    // Create a Date object from the date string
    const date = new Date(dateString);

    // Extract day, month, year, hours, minutes, and seconds
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    // Format the date and time as desired
    return `${day}/${month}/${year} at ${hours}:${minutes}:${seconds}`;
}

</script>

<template>

    <AppTheming />

    <JetReportingHeader :supervisor="supervisor" />

    <Head title="Reporting Dashboard" />

    <div id="content-wrapper" class="bg-gray-200 reporting-dashboard-content-wrapper">
        <div id="main-content" class="container w-full pt-5 pb-5 mx-auto">

            <div class="sm:pl-10 sm:pr-10">
                <div class="mt-0 text-gray-500">
                    <div class="mt-10 mb-10 form-item-container">

                        <h1 class="reporting-dashboard-title">
                            {{ $t('reporting.dashboard.title', { supervisor_first_name: supervisor.first_name, supervisor_last_name: supervisor.last_name }) }}
                        </h1>
                        
                        <p class="font-semibold">
                            {{ $t('reporting.dashboard.supervisor_email', { supervisor_email: supervisor.email }) }}
                        </p>

                        <p class="font-semibold" v-if="supervisor.supervisor_type == 'icb_supervisor'">
                            {{ $t('reporting.dashboard.assigned_icb_region', { assigned_icb_region: supervisorRegionOrOrg }) }}
                        </p>
                        <p class="font-semibold" v-else>
                            {{ $t('reporting.dashboard.assigned_private_org', { assigned_private_org: supervisorRegionOrOrg }) }}
                        </p>

                        <p v-if="supervisor.supervisor_type == 'icb_supervisor' && matchingTeamsGroupedbyOrganisation.length > 0">
                            {{ $t('reporting.dashboard.dashboard_intro_icb') }}
                        </p>
                        <p v-else-if="supervisor.supervisor_type == 'private_supervisor' && matchingTeamsGroupedbyOrganisation.length > 0">
                            {{ $t('reporting.dashboard.dashboard_intro_org') }}
                        </p>
                        <p v-else></p>

                        <div v-if="matchingTeamsGroupedbyOrganisation.length > 0"><!-- Check if there are matching organisations -->

                            <div class="organisation-team-group" v-for="organisation in matchingTeamsGroupedbyOrganisation" :key="organisation.organisation.id">
                            
                                <h2 class="organisation-name">{{ organisation.organisation.name }}</h2>

                                <div class="organisation-team-group-header flex justify-between">
                                    <div class="sm:w-2/3">
                                        <ul class="organisation-data list-none">
                                            <li v-if="organisation.toolkit">
                                                <span class="reporting-dashboard-icon material-icons-outlined">construction</span>
                                                <span>{{ $t('reporting.dashboard.dashboard_orgs.current_toolkit_title', { organisation: organisation.organisation.name }) }} <span class="data-value">{{ organisation.toolkit.name }}</span></span>
                                            </li>
                                            <li v-else>
                                                <span class="reporting-dashboard-icon material-icons-outlined">construction</span>
                                                <span>{{ $t('reporting.dashboard.dashboard_orgs.no_current_toolkit') }}</span>
                                            </li>
                                            <li v-if="organisation.toolkit && organisation.toolkit.display_carbon_savings == 1">
                                                <span class="reporting-dashboard-icon material-icons-outlined">show_chart</span>
                                                <span>{{ $t('reporting.dashboard.dashboard_orgs.total_carbon_saved_title') }} <span class="data-value">{{ organisation.totalCarbonForOrganisation }} kg CO<sub>2</sub></span></span>
                                            </li>
                                            <li v-else>
                                                <span class="reporting-dashboard-icon material-icons-outlined">show_chart</span>
                                                <span>{{ $t('reporting.dashboard.dashboard_orgs.carbon_savings_inactive') }}</span>
                                            </li>
                                        </ul>
                                    </div>
                                    <div v-if="organisation.toolkit && organisation.toolkit.display_carbon_savings == 1" class="sm:w-1/3 carbon-savings-widget-container">
                                        <ReportingCarbonSavingsWidget :totalCarbonForOrganisation="organisation.totalCarbonForOrganisation"/>
                                    </div>
                                </div>

                                <div v-if="organisation.teams.length > 0"> <!-- Check if there are teams -->
                                    <h3 class="teams-title">{{ $t('reporting.dashboard.dashboard_orgs.teams_accordion_title') }}</h3> 

                                    <TeamAccordion v-for="team in organisation.teams" 
                                    :key="team.team.id"
                                    :title="team.team.name" 
                                    :id="team.team.id"
                                    >

                                        <div class="inner-content-wrapper">
                                            <ul class="team-data list-none">
                                                <li v-if="team.actions_completed_by_team.length > 0">
                                                    <span class="reporting-dashboard-icon material-icons-outlined">checklist</span>
                                                    <span>{{ $t('reporting.dashboard.dashboard_teams.total_actions') }} <span class="data-value">{{ team.totalActionsCompleted }}</span></span>                                              
                                                </li>
                                                <li v-else>
                                                    <span class="reporting-dashboard-icon material-icons-outlined">checklist</span>
                                                    <span>{{ $t('reporting.dashboard.dashboard_teams.no_completed_actions') }}</span>
                                                </li>
                                                <li v-if="team.actions_completed_by_team.length > 0 && organisation.toolkit.scoring == 'Points'">
                                                    <span class="reporting-dashboard-icon material-icons-outlined">checklist</span>
                                                    <span>{{ $t('reporting.dashboard.dashboard_teams.total_points') }} <span class="data-value">{{ team.totalPoints }}</span></span>
                                                </li>
                                                <li v-if="organisation.toolkit && organisation.toolkit.display_carbon_savings == 1">
                                                    <span class="reporting-dashboard-icon material-icons-outlined">show_chart</span>
                                                    <span>{{ $t('reporting.dashboard.dashboard_teams.total_estimated_carbon') }} <span class="data-value">{{ team.totalCarbonSaving }} kg CO<sub>2</sub></span></span>
                                                </li>
                                                <li>
                                                    <span class="reporting-dashboard-icon material-icons-outlined">group</span>
                                                    <span>{{ $t('reporting.dashboard.dashboard_teams.users') }} <span class="data-value">{{ team.userCount }}</span></span>

                                                    </li>
                                                <li v-if="team.teamToolkitStatus && team.teamToolkitStatus.submitted == 1">
                                                    <span class="reporting-dashboard-icon material-icons-outlined">event_available</span>
                                                    <span>{{ $t('reporting.dashboard.dashboard_teams.toolkit_status_title') }} {{ $t('reporting.dashboard.dashboard_teams.toolkit_submitted_at') }} <span class="data-value">{{ formatDate(team.teamToolkitStatus.submitted_at) }}</span></span>
                                                </li>
                                                <li v-else>
                                                    <span class="reporting-dashboard-icon material-icons-outlined">event_busy</span>
                                                    <span>{{ $t('reporting.dashboard.dashboard_teams.toolkit_status_title') }} <span class="data-value">{{ $t('reporting.dashboard.dashboard_teams.toolkit_not_submitted') }}</span></span>
                                                </li>
                                            </ul>      
                                        </div>
    
                                    </TeamAccordion>

                                </div>
                                <p v-else>{{ $t('reporting.dashboard.dashboard_teams.no_teams') }}</p> <!-- Message when there are no teams -->
                            </div>

                        </div>
                        <div v-else>
                            <p>{{ $t('reporting.dashboard.dashboard_orgs.no_orgs') }}</p>
                        </div>


                    </div>
                </div>
            </div>

        </div>
    </div>

</template>

